import { Metamodel, IViolation, DataFlowParameterConfig } from 'shell-models-lib';
import { DataFlowDatasetSettings, DatasetFormatType } from './dataflow-dataset-settings';
import { stringIsNullOrEmpty, format } from 'app/shared/utils/string.util';

import { DataFlowSource, IsolationLevel, SQLSourceOptions } from '../dataflow-source';
import { DataFlowSink } from '../dataflow-sink';
import { DataFlowExpression } from '../dataflowexpression';
import { StringDataFlowType, BooleanDataFlowType } from '../dataflow-datatype';
import { TableActionOption } from './tabular-type-settings';
import { initializeDataflowSink } from 'app/model/dataflow/dataflow-utils';

/**
 * Validates that schema name is provided when Table input is selected.
 * Separate from other validators for independent "Fixed" badge tracking.
 */
function validateSchemaName(owner: SqlBaseSettings, value?: DataFlowExpression): IViolation[] | undefined {
    const settings = owner;
    const datasetFormat = settings.transform?.datasetSettings?.getFormat();
    const isWarehouse = datasetFormat === DatasetFormatType.Warehouse || settings.store === DatasetFormatType.Warehouse;
    const isSqlType = datasetFormat === DatasetFormatType.SqlDB || datasetFormat === DatasetFormatType.SynapseAnalytics;
    
    if (settings.transform?.isInlineDataset && settings.transform.datasetSettings && (isSqlType || isWarehouse)) {
        if (settings.format === SQLSourceOptions.Table && stringIsNullOrEmpty(value?.expression)) {
            return [{
                context: settings.transform,
                message: () => format(stringResources.RequiredTemplate, stringResources.Dataset_TableOptions_Create_Schema_Placeholder),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                redirectTo: { entity: settings.transform, attribute: { name: 'schemaName' } as any }
            }];
        }
    }
    return undefined;
}

/**
 * Validates that table name is provided when Table input is selected.
 * Separate from other validators for independent "Fixed" badge tracking.
 */
function validateTableName(owner: SqlBaseSettings, value?: DataFlowExpression): IViolation[] | undefined {
    const settings = owner;
    const datasetFormat = settings.transform?.datasetSettings?.getFormat();
    const isWarehouse = datasetFormat === DatasetFormatType.Warehouse || settings.store === DatasetFormatType.Warehouse;
    const isSqlType = datasetFormat === DatasetFormatType.SqlDB || datasetFormat === DatasetFormatType.SynapseAnalytics;
    
    if (settings.transform?.isInlineDataset && settings.transform.datasetSettings && (isSqlType || isWarehouse)) {
        if (settings.format === SQLSourceOptions.Table && stringIsNullOrEmpty(value?.expression)) {
            return [{
                context: settings.transform,
                message: () => format(stringResources.RequiredTemplate, stringResources.Table_name_Label),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                redirectTo: { entity: settings.transform, attribute: { name: 'tableName' } as any }
            }];
        }
    }
    return undefined;
}

/**
 * Validates that query is provided when Query input is selected (source only).
 * Separate from other validators for independent "Fixed" badge tracking.
 */
function validateQuery(owner: SqlBaseSettings, value?: DataFlowExpression): IViolation[] | undefined {
    const settings = owner;
    const datasetFormat = settings.transform?.datasetSettings?.getFormat();
    const isWarehouse = datasetFormat === DatasetFormatType.Warehouse || settings.store === DatasetFormatType.Warehouse;
    const isSqlType = datasetFormat === DatasetFormatType.SqlDB || datasetFormat === DatasetFormatType.SynapseAnalytics;
    
    if (settings.transform?.isInlineDataset && settings.transform.datasetSettings && (isSqlType || isWarehouse)) {
        if (settings.format === SQLSourceOptions.Query && settings.transform instanceof DataFlowSource && stringIsNullOrEmpty(value?.expression)) {
            return [{
                context: settings.transform,
                message: () => format(stringResources.RequiredTemplate, stringResources.QueryLabel),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                redirectTo: { entity: settings.transform, attribute: { name: 'query' } as any }
            }];
        }
    }
    return undefined;
}

/**
 * Validates that stored procedure name is provided when Stored procedure input is selected (source only).
 * Separate from other validators for independent "Fixed" badge tracking.
 */
function validateStoredProcName(owner: SqlBaseSettings, value?: DataFlowExpression): IViolation[] | undefined {
    const settings = owner;
    const datasetFormat = settings.transform?.datasetSettings?.getFormat();
    const isWarehouse = datasetFormat === DatasetFormatType.Warehouse || settings.store === DatasetFormatType.Warehouse;
    const isSqlType = datasetFormat === DatasetFormatType.SqlDB || datasetFormat === DatasetFormatType.SynapseAnalytics;
    
    if (settings.transform?.isInlineDataset && settings.transform.datasetSettings && (isSqlType || isWarehouse)) {
        if (settings.format === SQLSourceOptions.Procedure && settings.transform instanceof DataFlowSource && stringIsNullOrEmpty(value?.expression)) {
            return [{
                context: settings.transform,
                message: () => format(stringResources.RequiredTemplate, stringResources.StoredProcedureName),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                redirectTo: { entity: settings.transform, attribute: { name: 'storedProcName' } as any }
            }];
        }
    }
    return undefined;
}

@Metamodel.model({
    name: 'SqlBaseSettings',
    validator: SqlBaseSettings.validateSettings
})
export class SqlBaseSettings extends DataFlowDatasetSettings {
    @Metamodel.attribute({
    })
    public databaseName: DataFlowExpression;

    @Metamodel.attribute({
        constraint: { validator: validateSchemaName }
    })
    public schemaName: DataFlowExpression;

    @Metamodel.attribute({
        constraint: { validator: validateTableName }
    })
    public tableName: DataFlowExpression;

    @Metamodel.attribute({
    })
    public format: string;

    @Metamodel.attribute({
    })
    public preSQLScripts: DataFlowExpression[];

    public isPreSqlsListOfExpression: boolean = true;

    @Metamodel.attribute({
    })
    public postSQLScripts: DataFlowExpression[];

    public isPostSqlsListOfExpression: boolean = true;

    @Metamodel.attribute({
    })
    public store: string;

    @Metamodel.attribute({
    })
    public isolationLevel: DataFlowExpression;

    @Metamodel.attribute({
        constraint: { validator: validateQuery }
    })
    public query: DataFlowExpression;

    @Metamodel.attribute({
    })
    public storedProcInputs: DataFlowParameterConfig[];

    @Metamodel.attribute({
    })
    public storedProcResultSet: DataFlowExpression;

    @Metamodel.attribute({
        constraint: { validator: validateStoredProcName }
    })
    public storedProcName: DataFlowExpression;

    @Metamodel.attribute({
    })
    public tableActionOption: TableActionOption;

    @Metamodel.attribute({
    })
    public skipKeyWrites: DataFlowExpression;

    public useTmpDB: boolean;
    public stagingSchemaName: DataFlowExpression;

    public static validateSettings(settings: SqlBaseSettings): IViolation[] {
        const violations: IViolation[] = [];
        const datasetFormat = settings.transform.datasetSettings?.getFormat();
        const isWarehouse = datasetFormat === DatasetFormatType.Warehouse || settings.store === DatasetFormatType.Warehouse;
        const isSqlType = datasetFormat === DatasetFormatType.SqlDB || datasetFormat === DatasetFormatType.SynapseAnalytics;
        
        if (__IS_FABRIC_MDF__ && settings.transform.isInlineDataset && settings.transform.datasetSettings && (isSqlType || isWarehouse)) {
            // MDF Warehouse: Skip database validation - Warehouse artifact IS the database
            if (!isWarehouse && (!settings.databaseName || stringIsNullOrEmpty(settings.databaseName.expression))) {
                violations.push({
                    context: settings.transform,
                    message: () => format(stringResources.RequiredTemplate, stringResources.ModelName_DatabaseName),
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    redirectTo: { entity: settings.transform, attribute: { name: 'databaseName' } as any }
                });
            }
            // Note: schemaName, tableName, query, storedProcName validations moved to attribute-level validators
            // for independent "Fixed" badge tracking
        }

        return violations;
    }

    public get transform(): DataFlowSource | DataFlowSink {
        return this.owner instanceof DataFlowSource ? this.owner as DataFlowSource : this.owner as DataFlowSink;
    }

    public getFormat(): DatasetFormatType {
        return undefined;
    }

    public getSubFormat(): string {
        return undefined;
    }

    public getDatasetType(): string {
        return undefined;
    }

    public getIconClass(): string {
        return '';
    }

    public initialize(subFormat: string): void {
        this.format = 'table';
        this.preSQLScripts = [this.transform.createTemplatableExpression()];
        this.postSQLScripts = [this.transform.createTemplatableExpression()];
        this.databaseName = this.transform.createTemplatableExpression();
        this.schemaName = this.transform.createTemplatableExpression();
        this.tableName = this.transform.createTemplatableExpression();
        this.isolationLevel = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, IsolationLevel.ReadUncommitted);
        this.query = this.transform.createTemplatableExpression();
        this.storedProcInputs = [];
        this.storedProcResultSet = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
        this.storedProcName = this.transform.createTemplatableExpression();
        this.allowInsert = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
        this.allowUpdate = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.allowUpsert = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.allowDelete = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.keyColumns = [this.transform.createTemplatableExpression()];
        initializeDataflowSink(this);
        this.tableActionOption = TableActionOption.None;
        this.skipKeyWrites = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.useTmpDB = true;
        this.stagingSchemaName = this.transform.createTemplatableExpression();
    }
}
